import React from "react";
import {
    CardContent,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Box,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
    StyledCard,
    StyledTypography,
    StyledAccordion,
} from "components/Screens/Company/CompanySummary";
import Markdown from "components/Common/Markdown/Markdown";
import { SummaryVoting } from "../types";
import { getBQVotingItems, getMQVotingItems } from "../utils";
import { formatDate } from "utils/date.utils";

interface Props {
    votingData: SummaryVoting;
    title: string;
    type: "BQ" | "MQ";
}

const VotingSection: React.FC<Props> = ({ votingData, title, type }) => {
    if (!votingData) {
        return null;
    }

    const votingItems =
        type === "BQ"
            ? getBQVotingItems(votingData)
            : getMQVotingItems(votingData);

    return (
        <StyledCard autoHeight>
            <StyledAccordion
                defaultExpanded={true}
                variant="outlined"
                iconPosition="right"
                noBorder
            >
                <AccordionSummary
                    expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        className="card-accordion-title"
                        sx={{ width: "100%" }}
                    >
                        <StyledTypography
                            variant="h6"
                            color="primary"
                            fontWeight="bold"
                        >
                            {title}
                        </StyledTypography>
                        <Box
                            className={`voteScore score-${votingData.overall_score}`}
                        >
                            <StyledTypography variant="body2" fontWeight="bold">
                                {votingData.overall_score}
                            </StyledTypography>
                        </Box>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <CardContent sx={{ p: 0 }}>
                        <StyledTypography
                            variant="body2"
                            color="textSecondary"
                            mb={1}
                        >
                            {formatDate(new Date(votingData.voting_date))}
                        </StyledTypography>

                        <Markdown
                            text={votingData.voting_summary_markdown}
                            fontSize="16px"
                            lineHeight="1.5"
                        />
                    </CardContent>
                </AccordionDetails>
            </StyledAccordion>

            <Stack borderRadius={0}>
                {votingItems.filter((item) => item.score != null).map((item) => (
                    <StyledAccordion key={item.key} variant="outlined">
                        <AccordionSummary
                            sx={{
                                px: (theme) => theme.spacing(2) + " !important",
                            }}
                            expandIcon={
                                <FontAwesomeIcon icon={faChevronDown} />
                            }
                        >
                            <Box
                                display="flex"
                                sx={{
                                    alignItems: "center",
                                    gap: 1,
                                    width: "100%",
                                }}
                            >
                                <StyledTypography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                >
                                    {item.label}
                                </StyledTypography>
                                <Box
                                    className={`voteScore score-${item.score}`}
                                >
                                    <StyledTypography
                                        variant="body2"
                                        fontWeight="bold"
                                    >
                                        {item.score}
                                    </StyledTypography>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {item.comment && (
                                <Markdown text={item.comment} fontSize="16px" />
                            )}
                        </AccordionDetails>
                    </StyledAccordion>
                ))}
            </Stack>
        </StyledCard>
    );
};

export default VotingSection;
